import { ViewPermissions } from '@/components/Auth/ViewPermissions';
import { useIntl } from 'react-intl';
import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import Input from '@/components/core/Input.tsx';
import { FilterPanel } from '@/components/FilterPanel';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import Button from '@/components/core/Button';
import { VirtualizedList } from '@/components/VirtualizedList';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { ApplicationListItem } from './ApplicationListItem';
import { useAscOrDescIcon } from '@/hooks/useSort';
import { useStatusOptions } from '@/hooks/useStatusOptions';
import { Route as OrganizationsRoute } from '@/routes/$tenantId/_layout/organizations/$view';
import { createSortConfig } from '@/utils/sort';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { FilterType } from '@/types/filterSearch';
import { Loading } from '../Loading';
import { DataNotFound } from '../DataNotFound';
import {
  useListOrganizationApplications,
  OrganizationApplicationDTO,
} from '@/api/trafikportalen';
import { getAllOption } from '@/helpers/filter';

export function ApplicationsList() {
  const {
    query,
    filter: filterApplications,
    sort,
    direction,
  } = OrganizationsRoute.useSearch();

  const { data, isLoading } = useListOrganizationApplications();
  const rightPanelRef = useRef<HTMLElement>(null);

  const { formatMessage } = useIntl();
  const [announcement, setAnnouncement] = useState('');

  const sortConfigs = useMemo(() => {
    return createSortConfig<OrganizationApplicationDTO>({
      textFields: ['name', 'created_at'],
    });
  }, []);

  const applications = useMemo(() => {
    return data?.filter((application) => application.status !== 'approved');
  }, [data]);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearchParams,
    handleSearch,
  } = useSearchFilterSort<OrganizationApplicationDTO>({
    items: applications || [],
    searchKeys: ['name', 'organization_number'],
    sortConfigs,
    routePath: OrganizationsRoute.fullPath,
    defaultSearchParams: {
      sort: 'created_at',
      direction: 'desc',
      filter: 'submitted',
    },
  });

  const getStatus = useStatusOptions();
  const getIcon = useAscOrDescIcon();

  const options = useMemo((): OptionsType[] => {
    return [
      getAllOption(),
      {
        id: 'submitted',
        label: getStatus({ status: 'submitted' })?.label ?? '',
      },
      {
        id: 'rejected',
        label: getStatus({ status: 'rejected' })?.label ?? '',
      },
    ];
  }, [getStatus]);

  const filteredApplications = useMemo(() => {
    if (!sortedItems) {
      return [];
    }

    if (!filterSearch?.filter || filterSearch.filter.id === getAllOption().id) {
      return sortedItems;
    }

    return sortedItems.filter(
      (application) => application.status === filterSearch?.filter?.id,
    );
  }, [sortedItems, filterSearch]);

  const handleFilter = useCallback(
    (filter: OptionsType) => {
      setFilterSearch((prev) => ({
        ...prev!,
        filter:
          filter === prev?.filter ? getAllOption() : (filter as FilterType),
      }));
      handleSearchParams({
        filter: filter.id,
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const renderApplicationListItem = useCallback(
    (application: OrganizationApplicationDTO) => {
      return (
        <ApplicationListItem key={application.id} application={application} />
      );
    },
    [],
  );

  // Announce changes to sort and filter for screen readers
  useEffect(() => {
    if (!filterSearch) return;

    const count = filteredApplications.length;
    const sortLabel =
      filterSearch.sort === 'created_at'
        ? formatMessage({ defaultMessage: 'datum' })
        : formatMessage({ defaultMessage: 'namn' });
    const directionLabel =
      filterSearch.direction === 'asc'
        ? formatMessage({ defaultMessage: 'stigande' })
        : formatMessage({ defaultMessage: 'fallande' });

    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {ansökan} other {ansökningar}} sorterade efter {sortLabel} {directionLabel}',
      },
      { count, sortLabel, directionLabel },
    );

    setAnnouncement(message);
  }, [filterSearch, filteredApplications.length, formatMessage]);

  useEffect(() => {
    if (filterSearch?.filter) return;

    // Set initial filter search values
    const submittedOption =
      options.find((d) => d.id === 'submitted') ?? getAllOption();
    const applications = filterApplications
      ? ((options.find((d) => d.id === filterApplications) as FilterType) ??
        submittedOption)
      : submittedOption;

    setFilterSearch({
      query: query ?? '',
      filter: applications as FilterType,
      sort: sort ?? 'created_at',
      direction: direction ?? 'desc',
    });
  }, [
    options,
    direction,
    filterApplications,
    query,
    sort,
    filterSearch,
    setFilterSearch,
  ]);

  if (isLoading) {
    return <Loading />;
  }

  if (applications?.length === 0) {
    return (
      <DataNotFound
        description={formatMessage({
          defaultMessage: 'Finns inga ansökningar',
        })}
      />
    );
  }

  return (
    <ViewPermissions permission="tenant:viewer">
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <RightPanel ref={rightPanelRef}>
        {filterSearch && (
          <FilterPanel
            firstInput={
              <div className="flex w-full flex-col gap-8">
                <Input
                  type="text"
                  label="Sök"
                  placeholder="Sök på namn eller orgnummer"
                  id="organization-search"
                  value={filterSearch.query}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            }
            filterOptions={[
              <Dropdown
                key={options?.length}
                label="Status"
                id="application-status"
                options={options ?? []}
                placeholder="Status"
                value={filterSearch.filter}
                onValueChange={(value) => handleFilter(value)}
                aria-description="Filtrera ansökningar genom att välja status"
                containerRef={rightPanelRef}
              />,
            ]}
            sortingOptions={[
              <Button
                key={options?.length}
                variant={
                  filterSearch.sort === 'created_at' ? 'filled' : 'outline'
                }
                startIcon={getIcon({
                  selectedSort: filterSearch.sort!,
                  wantedSort: 'created_at',
                  sortDirection: filterSearch.direction!,
                })}
                onClick={() => handleSort('created_at')}
                aria-label={`Sortera efter datum ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                aria-pressed={filterSearch.sort === 'created_at'}
              >
                {formatMessage({
                  defaultMessage: 'Datum',
                })}
              </Button>,
              <Button
                key={options?.length}
                variant={filterSearch.sort === 'name' ? 'filled' : 'outline'}
                startIcon={getIcon({
                  selectedSort: filterSearch.sort!,
                  wantedSort: 'name',
                  sortDirection: filterSearch.direction!,
                })}
                onClick={() => handleSort('name')}
                aria-label={`Sortera efter namn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                aria-pressed={filterSearch.sort === 'name'}
              >
                {formatMessage({
                  defaultMessage: 'Namn',
                })}
              </Button>,
            ]}
          />
        )}
      </RightPanel>
      <VirtualizedList
        count={filteredApplications.length}
        countName={formatMessage({ defaultMessage: 'ansökningar' })}
        totalItems={applications ?? []}
        filteredItems={filteredApplications}
        renderItem={renderApplicationListItem}
        getItemKey={(application) => application.id}
      />
    </ViewPermissions>
  );
}
